import React from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  FileCheck2,
  Gauge,
  HelpCircle,
  Search,
  ShieldCheck,
  Tags,
  UserRoundCheck,
  UsersRound,
  Wrench,
} from "lucide-react";
import { useAuth } from "../../components/system/AppGate";

const UPDATED_AT = "23 de septiembre de 2026";

const ROLE_LABELS = {
  all: "Todo el manual",
  technician: "Para técnicos",
  manager: "Para jefe de laboratorio",
};

const STATUS_STEPS = [
  ["En depósito", "El instrumento fue recibido y está disponible para el laboratorio."],
  ["En proceso", "El técnico comenzó el trabajo y puede cargar mediciones."],
  ["Calibrado", "Las mediciones y los cálculos fueron finalizados."],
  ["Pendiente de aprobación", "El jefe debe revisar el certificado."],
  ["Revisado", "El certificado fue aprobado o el hito de revisión quedó registrado."],
  ["Listo para remitar", "El trabajo puede continuar por el circuito de salida."],
  ["Remitido / Entregado", "El instrumento salió del laboratorio. Una revisión posterior no lo hace retroceder."],
];

const SECTIONS = [
  {
    id: "circuito",
    role: "all",
    icon: Gauge,
    title: "Circuito completo de una calibración",
    summary: "Desde la orden asignada hasta la entrega del certificado.",
    keywords: "flujo circuito estados deposito proceso calibrado revisado remitado entregado",
    steps: [
      ["1. Localizar el trabajo", "Entrá en Operaciones → Mis OS. El jefe puede seleccionar un técnico para consultar sus trabajos."],
      ["2. Revisar el instrumento", "Abrí la orden y comprobá cliente, tipo de equipo, marca, modelo, número de serie, TAG, rango y servicio solicitado."],
      ["3. Comenzar la calibración", "Abrí la ficha del instrumento y usá Calibrar. El sistema selecciona la plantilla según la variable y el tipo de equipo."],
      ["4. Cargar y calcular", "Registrá condiciones ambientales, patrón utilizado y lecturas. Guardá el borrador y ejecutá el cálculo final."],
      ["5. Revisar y emitir", "El resultado pasa a Certificados a aprobar. El jefe revisa la vista previa y decide aprobar o rechazar."],
      ["6. Consultar y entregar", "El certificado aprobado queda en la ficha, el buscador, el portal del cliente y el enlace QR."],
    ],
    links: [{ to: "/ops/my-pos", label: "Abrir OS del laboratorio" }],
  },
  {
    id: "tecnico-trabajo",
    role: "technician",
    icon: Wrench,
    title: "Trabajo diario del técnico",
    summary: "Cómo tomar una OS, calibrar y dejarla lista para revisión.",
    keywords: "tecnico mis os comenzar mediciones borrador calculo finalizar prioridad etiqueta",
    steps: [
      ["Elegir la OS", "Revisá prioridad y datos del trabajo. No cambies de instrumento hasta confirmar que la ficha corresponde al equipo físico."],
      ["Controlar identificación", "Compará el TAG y el número de serie. Si falta un dato o no coincide, corregilo antes de emitir el certificado."],
      ["Seleccionar el patrón", "Usá solamente patrones vigentes y aptos para el rango. Si no aparecen opciones, informá al jefe; no sustituyas el patrón por uno escrito manualmente."],
      ["Cargar las lecturas", "Completá todos los puntos y respetá el orden de las series ascendentes y descendentes cuando la plantilla lo solicite."],
      ["Guardar borrador", "Guardá durante la carga para no perder el trabajo. El borrador no envía todavía el certificado al jefe."],
      ["Calcular y finalizar", "Revisá los resultados, la unidad, la tolerancia y la conformidad. Al finalizar, la calibración queda disponible para aprobación."],
    ],
    notes: [
      "No copies resultados calculados desde Excel: cargá las mediciones originales y dejá que el sistema realice el cálculo.",
      "Si el jefe rechaza el certificado, el motivo queda registrado y el instrumento vuelve al técnico para corregirlo.",
    ],
    links: [{ to: "/ops/my-pos", label: "Ir a Mis OS" }],
  },
  {
    id: "plantillas",
    role: "technician",
    icon: ClipboardCheck,
    title: "Uso de las plantillas de calibración",
    summary: "Controles comunes para presión, dimensional, eléctrica y las demás variables.",
    keywords: "plantillas presión dimensional micrometro comparador soldadura temperatura caudal masa fuerza torque gases valvulas electrica",
    steps: [
      ["Datos iniciales", "Confirmá rango, resolución, unidad, tolerancia o clase del instrumento. Estos datos condicionan los puntos y la evaluación."],
      ["Condiciones ambientales", "Registrá los valores reales del lugar de calibración; no reutilices automáticamente los de otra sesión."],
      ["Trazabilidad", "Seleccioná el patrón ofrecido por la aplicación y comprobá que su certificado esté vigente y cubra el rango utilizado."],
      ["Puntos y repeticiones", "Completá todas las lecturas solicitadas. Un punto incompleto puede impedir el cálculo o producir un certificado incompleto."],
      ["Resultado", "Antes de finalizar, revisá error, incertidumbre, tolerancia y conformidad. Si un valor resulta extraño, verificá primero la unidad y la lectura ingresada."],
    ],
    notes: [
      "Las fórmulas del sistema prevalecen sobre planillas históricas que tengan referencias rotas o valores escritos manualmente.",
      "No emitas un certificado si el procedimiento o el criterio técnico todavía necesita aprobación del responsable del laboratorio.",
    ],
  },
  {
    id: "certificado-externo",
    role: "technician",
    icon: FileCheck2,
    title: "Cargar un certificado externo o especial",
    summary: "Para instrumentos cuyo certificado fue confeccionado fuera de la aplicación.",
    keywords: "subir cargar externo especial pdf numero fecha vencimiento archivo",
    steps: [
      ["Abrir la opción", "Desde la orden o la ficha del instrumento elegí Subir certificado (especial)."],
      ["Completar los datos", "Seleccioná un PDF e ingresá el número de certificado. Completá fecha de calibración y vencimiento cuando correspondan."],
      ["Enviar para aprobación", "El archivo queda pendiente. Todavía no está disponible para el cliente."],
      ["Esperar la revisión", "El jefe abre el PDF original y lo aprueba o rechaza desde Certificados a aprobar."],
    ],
    notes: ["Esta opción no reemplaza el circuito normal cuando existe una plantilla de calibración en el sistema."],
  },
  {
    id: "jefe-control",
    role: "manager",
    icon: UsersRound,
    title: "Control del trabajo del laboratorio",
    summary: "Seguimiento por técnico, prioridades y avance de las órdenes.",
    keywords: "jefe laboratorio técnicos trabajos asignados indicadores kpi prioridad seguimiento os",
    steps: [
      ["Seleccionar un técnico", "En OS del laboratorio elegí al técnico. La selección se conserva al abrir una orden y regresar."],
      ["Revisar el avance", "Usá los indicadores para detectar trabajos en depósito, en proceso, calibrados, revisados o pendientes de salida."],
      ["Abrir la orden", "Comprobá sus instrumentos, datos técnicos y estado. Priorizá las correcciones que bloqueen certificados o remitos."],
      ["Actualizar", "Usá Actualizar trabajos cuando necesites consultar nuevamente el estado guardado en el sistema."],
    ],
    links: [{ to: "/ops/my-pos", label: "Ver OS del laboratorio" }],
  },
  {
    id: "jefe-aprobacion",
    role: "manager",
    icon: UserRoundCheck,
    title: "Revisar, aprobar o rechazar certificados",
    summary: "La emisión definitiva siempre queda bajo control del jefe.",
    keywords: "jefe aprobar rechazar vista previa certificado pdf emisión revisión motivo",
    steps: [
      ["Abrir la bandeja", "Entrá en Certificados a aprobar y seleccioná un trabajo pendiente."],
      ["Revisar la vista previa", "Controlá identificación, cliente, fechas, patrón, condiciones ambientales, resultados, incertidumbre, conformidad y presentación del PDF."],
      ["Aprobar", "La aplicación genera o valida el PDF definitivo, lo registra y marca la revisión. El documento queda disponible para consulta."],
      ["Rechazar", "Escribí un motivo concreto. El técnico verá la observación y podrá corregir la sesión antes de enviarla nuevamente."],
    ],
    notes: [
      "Si el equipo ya fue remitido o entregado, aprobar después el certificado registra la revisión pero no hace retroceder el estado del instrumento.",
      "No apruebes únicamente por la apariencia del PDF: revisá también los resultados técnicos y la trazabilidad.",
    ],
    links: [{ to: "/ops/certificate-approvals", label: "Abrir certificados a aprobar" }],
  },
  {
    id: "patrones",
    role: "manager",
    icon: ShieldCheck,
    title: "Patrones y trazabilidad",
    summary: "Cómo mantener disponibles los patrones usados por las plantillas.",
    keywords: "patrones trazabilidad certificado vigente incertidumbre rango calidad capacidad",
    steps: [
      ["Mantener el instrumento patrón", "En Calidad → Instrumentos Patrones verificá identificación, certificado, vigencia, rango y capacidades."],
      ["Cargar resultados certificados", "Registrá los puntos e incertidumbres exactamente como figuran en el certificado del patrón."],
      ["Verificar", "Solo un patrón verificado, vigente y compatible debe aparecer como opción durante la calibración."],
      ["Renovación", "Cuando llegue un certificado nuevo, conservá el historial anterior y registrá la nueva vigencia; no sobrescribas el documento histórico."],
    ],
    links: [{ to: "/quality/reference-standards", label: "Abrir Instrumentos Patrones" }],
  },
  {
    id: "buscar-certificados",
    role: "manager",
    icon: Search,
    title: "Buscar, descargar y archivar certificados",
    summary: "Consulta operativa y resguardo documental.",
    keywords: "buscar descargar certificado cliente serie tag fecha qr archivo zip drive",
    steps: [
      ["Buscar", "Usá número de certificado, OS, cliente, TAG, serie, tipo o fecha para localizar el instrumento."],
      ["Descargar o reimprimir", "Desde el resultado podés descargar el PDF aprobado o volver a imprimir la etiqueta con QR."],
      ["Preparar el archivo", "El módulo de archivo permite preparar lotes protegidos para su resguardo institucional."],
      ["Confirmar el respaldo", "La descarga local no es el respaldo final. Confirmá el archivo solamente después de recuperar y verificar la copia guardada en Drive."],
    ],
    links: [
      { to: "/ops/certificates", label: "Buscar certificados" },
      { to: "/ops/certificate-archive", label: "Abrir archivo de certificados" },
    ],
  },
  {
    id: "etiquetas",
    role: "all",
    icon: Tags,
    title: "Impresión de etiquetas",
    summary: "Selección de equipos y uso de TAG o número de serie.",
    keywords: "etiquetas imprimir tag serie qr brother seleccionar",
    steps: [
      ["Abrir el impresor", "Desde Operaciones podés preparar las etiquetas de una orden o abrir el impresor manual."],
      ["Seleccionar", "Marcá únicamente los equipos que deban imprimirse."],
      ["Elegir identificación", "Seleccioná TAG o número de serie. Si falta el dato elegido, completalo o quitá esa etiqueta de la selección."],
      ["Comprobar", "Antes de imprimir revisá certificado, identificación, fechas y contenido del QR."],
    ],
  },
  {
    id: "problemas",
    role: "all",
    icon: HelpCircle,
    title: "Problemas frecuentes",
    summary: "Qué revisar antes de informar una incidencia.",
    keywords: "problemas error no aparece patrón guardar certificado pendiente internet rechazo permiso",
    issues: [
      ["No aparece el botón Calibrar", "Revisá la variable y el tipo de equipo de la ficha. Si la clasificación es incorrecta, debe corregirse antes de continuar."],
      ["No aparece ningún patrón", "El patrón puede estar vencido, sin verificar, fuera de rango o sin resultados certificados cargados. Informá al jefe."],
      ["No permite calcular", "Buscá campos obligatorios vacíos, puntos incompletos, unidades incorrectas o condiciones ambientales faltantes."],
      ["El certificado no aparece para aprobar", "Confirmá que se ejecutó el cálculo final y no solamente Guardar borrador. Actualizá la bandeja después."],
      ["El certificado externo no sube", "Comprobá que sea PDF, que tenga número de certificado y que la conexión esté activa."],
      ["El jefe rechazó el certificado", "Abrí nuevamente la calibración, leé el motivo, corregí los datos y volvé a finalizar."],
      ["Se interrumpió Internet", "No cierres la pantalla hasta recuperar la conexión. Confirmá que el último guardado figure correctamente antes de continuar."],
      ["Un estado parece incorrecto", "Actualizá la pantalla y revisá el historial. No fuerces un estado para ocultar el problema; informá la OS y el instrumento afectados."],
    ],
  },
];

function normalize(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function StepList({ rows }) {
  return (
    <ol className="space-y-3">
      {rows.map(([title, text], index) => (
        <li key={title} className="flex gap-3">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-500/15 text-xs font-bold text-indigo-200">
            {index + 1}
          </span>
          <div>
            <p className="font-medium text-slate-100">{title}</p>
            <p className="mt-0.5 text-sm leading-6 text-slate-300">{text}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function IssueList({ rows }) {
  return (
    <div className="divide-y divide-white/10 rounded-xl border border-white/10">
      {rows.map(([title, text]) => (
        <div key={title} className="p-4 sm:grid sm:grid-cols-[220px_1fr] sm:gap-5">
          <p className="font-medium text-amber-100">{title}</p>
          <p className="mt-1 text-sm leading-6 text-slate-300 sm:mt-0">{text}</p>
        </div>
      ))}
    </div>
  );
}

function ManualSection({ section, defaultOpen }) {
  const Icon = section.icon;
  return (
    <details
      id={section.id}
      open={defaultOpen}
      className="group scroll-mt-28 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/35"
    >
      <summary className="flex cursor-pointer list-none items-start gap-3 p-4 sm:p-5 [&::-webkit-details-marker]:hidden">
        <span className="rounded-xl border border-indigo-400/20 bg-indigo-500/10 p-2 text-indigo-200">
          <Icon className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-white">{section.title}</span>
          <span className="mt-1 block text-sm text-slate-400">{section.summary}</span>
        </span>
        <ChevronRight className="mt-2 h-5 w-5 shrink-0 text-slate-500 transition-transform group-open:rotate-90" />
      </summary>

      <div className="border-t border-white/10 px-4 py-5 sm:px-5">
        {section.steps && <StepList rows={section.steps} />}
        {section.issues && <IssueList rows={section.issues} />}

        {section.notes?.length ? (
          <div className="mt-5 space-y-2 rounded-xl border border-amber-400/20 bg-amber-500/[0.07] p-4">
            {section.notes.map((note) => (
              <div key={note} className="flex gap-2 text-sm leading-6 text-amber-50/90">
                <AlertTriangle className="mt-1 h-4 w-4 shrink-0 text-amber-300" />
                <span>{note}</span>
              </div>
            ))}
          </div>
        ) : null}

        {section.links?.length ? (
          <div className="mt-5 flex flex-wrap gap-2">
            {section.links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-400/30 bg-indigo-500/10 px-3 py-2 text-sm font-medium text-indigo-100 hover:bg-indigo-500/20"
              >
                {link.label}
                <ChevronRight className="h-4 w-4" />
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    </details>
  );
}

export default function OperationsManualPage() {
  const { role } = useAuth();
  const initialRole = role === "technician" ? "technician" : role === "lab_manager" ? "manager" : "all";
  const [audience, setAudience] = React.useState(initialRole);
  const [query, setQuery] = React.useState("");
  const normalizedQuery = normalize(query.trim());

  const visibleSections = React.useMemo(
    () =>
      SECTIONS.filter((section) => {
        const roleMatches = audience === "all" || section.role === "all" || section.role === audience;
        if (!roleMatches) return false;
        if (!normalizedQuery) return true;
        const haystack = normalize(
          [
            section.title,
            section.summary,
            section.keywords,
            ...(section.steps || []).flat(),
            ...(section.issues || []).flat(),
            ...(section.notes || []),
          ].join(" "),
        );
        return haystack.includes(normalizedQuery);
      }),
    [audience, normalizedQuery],
  );

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-2xl border border-indigo-400/20 bg-gradient-to-br from-indigo-500/15 via-slate-950/40 to-cyan-500/10 p-5 sm:p-7">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-indigo-300/20 bg-indigo-500/10 px-3 py-1 text-xs font-medium text-indigo-100">
              <BookOpen className="h-4 w-4" />
              Consulta dentro de la aplicación
            </div>
            <h1 className="text-2xl font-bold text-white sm:text-3xl">Manual de Operaciones</h1>
            <p className="mt-2 leading-7 text-slate-300">
              Guía de trabajo para técnicos y jefe de laboratorio: órdenes, calibraciones, aprobación,
              certificados, etiquetas y solución de problemas frecuentes.
            </p>
            <p className="mt-3 text-xs text-slate-500">Versión 1.0 · Actualizado el {UPDATED_AT}</p>
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm sm:min-w-[330px]">
            <div className="rounded-xl border border-white/10 bg-black/20 p-3">
              <p className="text-slate-400">Dirigido a</p>
              <p className="mt-1 font-medium text-white">Técnicos y jefe</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/20 p-3">
              <p className="text-slate-400">Alcance</p>
              <p className="mt-1 font-medium text-white">Fase 2 · Operaciones</p>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-white/10 bg-white/[0.035] p-4 sm:p-5">
        <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-slate-200">Buscar en el manual</span>
            <span className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Ej.: patrón, certificado externo, etiqueta, error…"
                className="w-full rounded-xl border border-white/10 bg-slate-950/60 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-indigo-400/50 focus:outline-none"
              />
            </span>
          </label>

          <div className="flex flex-wrap gap-2" aria-label="Filtrar manual por rol">
            {Object.entries(ROLE_LABELS).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setAudience(value)}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  audience === value
                    ? "bg-indigo-500 text-white"
                    : "border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {!normalizedQuery && audience === "all" ? (
        <section className="rounded-2xl border border-white/10 bg-slate-950/30 p-4 sm:p-5">
          <div className="mb-4 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-300" />
            <h2 className="font-semibold text-white">Estados principales del servicio</h2>
          </div>
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {STATUS_STEPS.map(([title, text], index) => (
              <div key={title} className="rounded-xl border border-white/10 bg-white/[0.035] p-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/15 text-xs font-bold text-emerald-200">
                    {index + 1}
                  </span>
                  <p className="font-medium text-slate-100">{title}</p>
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-400">{text}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="space-y-3">
        {visibleSections.map((section, index) => (
          <ManualSection
            key={section.id}
            section={section}
            defaultOpen={Boolean(normalizedQuery) || index === 0}
          />
        ))}

        {!visibleSections.length ? (
          <div className="rounded-2xl border border-dashed border-white/15 p-8 text-center">
            <HelpCircle className="mx-auto h-8 w-8 text-slate-500" />
            <p className="mt-3 font-medium text-slate-200">No encontramos ese tema</p>
            <p className="mt-1 text-sm text-slate-400">Probá con otra palabra o seleccioná Todo el manual.</p>
          </div>
        ) : null}
      </section>

      <section className="rounded-2xl border border-sky-400/20 bg-sky-500/[0.06] p-4 text-sm leading-6 text-sky-50/90">
        <p className="font-semibold text-sky-100">Cuándo informar una incidencia</p>
        <p className="mt-1">
          Si el dato guardado no coincide con el instrumento, un estado retrocede, un cálculo no puede explicarse
          o un PDF muestra información incorrecta, detené ese caso y comunicá la OS, el instrumento, la pantalla y
          el mensaje observado. No modifiques otro registro para compensarlo.
        </p>
      </section>
    </div>
  );
}
